import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ConferenceProceedings.css';

const ConferenceProceedings = () => {
  const { t } = useTranslation();
  const [patents, setPatents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    year_from: 2000,
    year_to: 2025,
    owner_type: '',
    title: '',
    pct: '',
    pct_number: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [patentsPerPage] = useState(12); // Show 12 patents per page
  const [expandedSummaries, setExpandedSummaries] = useState(new Set());

  // Function to decode HTML entities and clean HTML tags
  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value
      .replace(/<p>/g, '') // Remove opening <p> tags
      .replace(/<\/p>/g, '\n') // Replace closing </p> tags with newlines
      .replace(/<br\s*\/?>/g, '\n') // Replace <br> tags with newlines
      .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
      .replace(/\n\s*\n/g, '\n') // Remove multiple consecutive newlines
      .trim(); // Remove leading/trailing whitespace
  };

  const fetchPatents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const apiUrl = `https://e-services.inapi.org/api/patents?${params}`;
      console.log('🔍 Fetching patents from:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 API Response:', data);
      console.log('📊 Total patents found:', data.length);
      console.log('📊 First patent:', data[0]);
      console.log('📊 Search parameters used:', params.toString());
      
      setPatents(data);
    } catch (error) {
      console.error('❌ Error fetching patents:', error);
      setPatents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatents();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchPatents();
  };

  const handleInputChange = (e) => {
    setSearchParams({
      ...searchParams,
      [e.target.name]: e.target.value
    });
  };

  // Pagination logic
  const indexOfLastPatent = currentPage * patentsPerPage;
  const indexOfFirstPatent = indexOfLastPatent - patentsPerPage;
  const currentPatents = patents.slice(indexOfFirstPatent, indexOfLastPatent);
  const totalPages = Math.ceil(patents.length / patentsPerPage);

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Toggle summary expansion
  const toggleSummary = (patentIndex) => {
    const newExpanded = new Set(expandedSummaries);
    if (newExpanded.has(patentIndex)) {
      newExpanded.delete(patentIndex);
    } else {
      newExpanded.add(patentIndex);
    }
    setExpandedSummaries(newExpanded);
  };

  return (
    <div className="container-fluid px-4 mt-4" style={{ backgroundColor: '#fbfbfb', minHeight: '100vh' }}>
      <div className="row">
        <div className="col-12">
          {/* Hero Section */}
          <div className="inapi-hero-section mb-5">
            <div className="inapi-hero-content">
              <h1 className="display-3 fw-bold mb-3">
                <i className="fas fa-lightbulb me-3"></i>
                {t('patents.heroTitle')}
              </h1>
              <p className="lead mb-0">
                {t('patents.heroSubtitle')}
              </p>
            </div>
          </div>

          {/* Search Form */}
          <div className="card shadow-lg mb-5 border-0" style={{ borderRadius: '20px', backgroundColor: '#ffffff' }}>
            <div className="card-header text-white" style={{ background: 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)', borderRadius: '20px 20px 0 0' }}>
              <h5 className="mb-0 fw-bold">
                <i className="fas fa-search me-2"></i>
                {t('patents.searchTitle')}
              </h5>
            </div>
            <div className="card-body p-4" style={{ backgroundColor: '#ffffff' }}>
              <form onSubmit={handleSearch}>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold" style={{ color: '#2a2a2a' }}>
                      <i className="fas fa-calendar-alt me-1" style={{ color: '#e12a2f' }}></i>
                      {t('patents.yearFrom')}
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg border-2"
                      name="year_from"
                      value={searchParams.year_from}
                      onChange={handleInputChange}
                      min="2000"
                      max="2025"
                      style={{ borderRadius: '15px', borderColor: '#e12a2f', backgroundColor: '#fbfbfb' }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold" style={{ color: '#2a2a2a' }}>
                      <i className="fas fa-calendar-alt me-1" style={{ color: '#e12a2f' }}></i>
                      {t('patents.yearTo')}
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg border-2"
                      name="year_to"
                      value={searchParams.year_to}
                      onChange={handleInputChange}
                      min="2000"
                      max="2025"
                      style={{ borderRadius: '15px', borderColor: '#e12a2f', backgroundColor: '#fbfbfb' }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold" style={{ color: '#2a2a2a' }}>
                      <i className="fas fa-building me-1" style={{ color: '#e12a2f' }}></i>
                      {t('patents.ownerType')}
                    </label>
                    <select
                      className="form-select form-select-lg border-2"
                      name="owner_type"
                      value={searchParams.owner_type}
                      onChange={handleInputChange}
                      style={{ borderRadius: '15px', borderColor: '#e12a2f', backgroundColor: '#fbfbfb' }}
                    >
                      <option value="">{t('patents.allTypes')}</option>
                      <option value="university">{t('patents.university')}</option>
                      <option value="other">{t('patents.other')}</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold" style={{ color: '#2a2a2a' }}>
                      <i className="fas fa-globe me-1" style={{ color: '#e12a2f' }}></i>
                      {t('patents.pct')}
                    </label>
                    <select
                      className="form-select form-select-lg border-2"
                      name="pct"
                      value={searchParams.pct}
                      onChange={handleInputChange}
                      style={{ borderRadius: '15px', borderColor: '#e12a2f', backgroundColor: '#fbfbfb' }}
                    >
                      <option value="">{t('patents.allTypes')}</option>
                      <option value="yes">{t('patents.yes')}</option>
                      <option value="no">{t('patents.no')}</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold" style={{ color: '#2a2a2a' }}>
                      <i className="fas fa-hashtag me-1" style={{ color: '#e12a2f' }}></i>
                      {t('patents.pctNumber')}
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg border-2"
                      name="pct_number"
                      value={searchParams.pct_number}
                      onChange={handleInputChange}
                      placeholder={t('patents.pctPlaceholder')}
                      style={{ borderRadius: '15px', borderColor: '#e12a2f', backgroundColor: '#fbfbfb' }}
                    />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label fw-semibold" style={{ color: '#2a2a2a' }}>
                      <i className="fas fa-search me-1" style={{ color: '#e12a2f' }}></i>
                      {t('patents.searchInTitle')}
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg border-2"
                      name="title"
                      value={searchParams.title}
                      onChange={handleInputChange}
                      placeholder={t('patents.titlePlaceholder')}
                      style={{ borderRadius: '15px', borderColor: '#e12a2f', backgroundColor: '#fbfbfb' }}
                    />
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button type="submit" className="btn btn-lg w-100 fw-bold" disabled={loading} style={{ borderRadius: '15px', background: 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)', border: 'none', color: 'white', boxShadow: '0 4px 15px rgba(225, 42, 47, 0.3)' }}>
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin me-2"></i>
                          {t('patents.searching')}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-search me-2"></i>
                          {t('patents.search')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Results */}
          <div className="row">
            {loading ? (
              <div className="col-12 text-center py-5">
                <div className="d-flex justify-content-center mb-4">
                  <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: '#e12a2f' }}>
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                </div>
                <h4 className="mb-3" style={{ color: '#2a2a2a' }}>{t('patents.loading')}</h4>
                <p style={{ color: '#2a2a2a' }}>{t('patents.loadingDescription')}</p>
              </div>
            ) : patents.length > 0 ? (
              <>
                {/* Results count */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0" style={{ color: '#2a2a2a' }}>
                        <i className="fas fa-list me-2" style={{ color: '#e12a2f' }}></i>
                        {patents.length} {t('patents.resultsFound')}
                      </h5>
                      <span style={{ color: '#2a2a2a' }}>
                        {t('patents.page')} {currentPage} {t('patents.of')} {totalPages}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Patents Grid */}
                {currentPatents.map((patent, index) => {
                console.log(`🔍 Patent ${index}:`, patent);
                return (
                  <div key={index} className="col-lg-6 col-xl-4 mb-4">
                    <div className="card h-100 shadow-lg border-0" style={{ borderRadius: '20px', transition: 'all 0.3s ease', background: 'linear-gradient(145deg, #ffffff 0%, #fbfbfb 100%)', border: '1px solid rgba(225, 42, 47, 0.1)' }}
                         onMouseEnter={(e) => {
                           e.currentTarget.style.transform = 'translateY(-8px)';
                           e.currentTarget.style.boxShadow = '0 25px 50px rgba(225, 42, 47, 0.15)';
                           e.currentTarget.style.borderColor = '#e12a2f';
                         }}
                         onMouseLeave={(e) => {
                           e.currentTarget.style.transform = 'translateY(0)';
                           e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                           e.currentTarget.style.borderColor = 'rgba(225, 42, 47, 0.1)';
                         }}>
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h5 className="card-title fw-bold mb-0" style={{ fontSize: '1.1rem', lineHeight: '1.3', color: '#2a2a2a' }}>
                            <i className="fas fa-certificate me-2" style={{ color: '#e12a2f' }}></i>
                            {decodeHtmlEntities(patent.title || patent.TITLE || patent.titre || 'Titre non disponible')}
                          </h5>
                          <span className="badge rounded-pill" style={{ background: 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)', fontSize: '0.75rem', color: 'white' }}>
                            <i className="fas fa-file-alt me-1"></i>
                            {t('patents.patent')}
                          </span>
                        </div>
                        
                        <div className="patent-details mb-3">
                          <div className="row g-2">
                            <div className="col-12">
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-user text-primary me-2" style={{ width: '20px' }}></i>
                                <span className="fw-semibold text-dark">{t('patents.owner')}</span>
                                <span className="ms-2 text-muted">{decodeHtmlEntities(patent.person_name || patent.PERSON_NAME || patent.proprietaire || t('patents.notSpecified'))}</span>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-calendar text-primary me-2" style={{ width: '20px' }}></i>
                                <span className="fw-semibold text-dark">{t('patents.date')}</span>
                                <span className="ms-2 text-muted">{patent.registration_date || patent.REGISTRATION_DATE || patent.date_enregistrement || t('patents.unknownDate')}</span>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="d-flex align-items-center mb-2">
                                <i className="fas fa-hashtag text-primary me-2" style={{ width: '20px' }}></i>
                                <span className="fw-semibold text-dark">{t('patents.fileNumber')}</span>
                                <span className="ms-2 text-muted">{patent.file_nbr || patent.FILE_NBR || patent.numero_fichier || t('patents.na')}</span>
                              </div>
                            </div>
                            {(patent.pct_appl_id || patent.PCT_APPL_ID) && (
                              <div className="col-12">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-globe text-success me-2" style={{ width: '20px' }}></i>
                                  <span className="fw-semibold text-dark">PCT:</span>
                                  <span className="ms-2 text-success fw-semibold">{patent.pct_appl_id || patent.PCT_APPL_ID}</span>
                                </div>
                              </div>
                            )}
                            {(patent.ipc_section_code || patent.IPC_SECTION_CODE) && (
                              <div className="col-12">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-tags text-info me-2" style={{ width: '20px' }}></i>
                                  <span className="fw-semibold text-dark">CIB:</span>
                                  <span className="ms-2 text-info fw-semibold">{patent.ipc_section_code || patent.IPC_SECTION_CODE}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {(patent.summary || patent.SUMMARY || patent.resume) && (
                          <div className="mt-3 p-3" style={{ background: 'rgba(102, 126, 234, 0.1)', borderRadius: '10px', border: '1px solid rgba(102, 126, 234, 0.2)' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="text-primary fw-semibold mb-0">
                                <i className="fas fa-file-text me-1"></i>
                                {t('patents.summary')}
                              </h6>
                              <i className={`fas fa-chevron-${expandedSummaries.has(indexOfFirstPatent + index) ? 'up' : 'down'} text-primary`}></i>
                            </div>
                            <div 
                              className="small text-muted" 
                              style={{ 
                                lineHeight: '1.5',
                                maxHeight: expandedSummaries.has(indexOfFirstPatent + index) ? 'none' : '60px',
                                overflow: expandedSummaries.has(indexOfFirstPatent + index) ? 'visible' : 'hidden',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {decodeHtmlEntities(patent.summary || patent.SUMMARY || patent.resume)}
                            </div>
                            {!expandedSummaries.has(indexOfFirstPatent + index) && (
                              <div 
                                className="text-center mt-2"
                                onClick={() => toggleSummary(indexOfFirstPatent + index)}
                                style={{ cursor: 'pointer' }}
                              >
                                <small className="text-primary fw-semibold">
                                  <i className="fas fa-chevron-down me-1"></i>
                                  {t('patents.clickToExpand')}
                                </small>
                              </div>
                            )}
                            {expandedSummaries.has(indexOfFirstPatent + index) && (
                              <div 
                                className="text-center mt-2"
                                onClick={() => toggleSummary(indexOfFirstPatent + index)}
                                style={{ cursor: 'pointer' }}
                              >
                                <small className="text-primary fw-semibold">
                                  <i className="fas fa-chevron-up me-1"></i>
                                  {t('patents.clickToCollapse')}
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="card-footer bg-transparent border-0 p-3 pt-0">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">
                            <i className="fas fa-calendar-alt me-1"></i>
                            {patent.registration_date || patent.REGISTRATION_DATE || patent.date_enregistrement || 'Date inconnue'}
                          </small>
                          <div className="d-flex gap-2">
                            {(patent.pct_appl_id || patent.PCT_APPL_ID) && (
                              <span className="badge bg-success">
                                <i className="fas fa-globe me-1"></i>
                                PCT
                              </span>
                            )}
                            {(patent.ipc_section_code || patent.IPC_SECTION_CODE) && (
                              <span className="badge bg-info">
                                <i className="fas fa-tags me-1"></i>
                                CIB
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
                }                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="row mt-5">
                    <div className="col-12">
                      <nav aria-label="Pagination des brevets">
                        <ul className="pagination justify-content-center">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link border-0" 
                              onClick={handlePrevPage}
                              disabled={currentPage === 1}
                              style={{ borderRadius: '15px', margin: '0 2px', background: currentPage === 1 ? '#fbfbfb' : 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)', color: currentPage === 1 ? '#2a2a2a' : 'white', border: '1px solid rgba(225, 42, 47, 0.2)' }}
                            >
                              <i className="fas fa-chevron-left me-1"></i>
                              Précédent
                            </button>
                          </li>
                          
                          {/* Page numbers */}
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                            // Show only a few pages around current page
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                            ) {
                              return (
                                <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                  <button
                                    className="page-link border-0"
                                    onClick={() => handlePageChange(pageNumber)}
                                    style={{ 
                                      borderRadius: '15px', 
                                      margin: '0 2px',
                                      background: currentPage === pageNumber 
                                        ? 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)' 
                                        : '#fbfbfb',
                                      color: currentPage === pageNumber ? 'white' : '#2a2a2a',
                                      border: '1px solid rgba(225, 42, 47, 0.2)'
                                    }}
                                  >
                                    {pageNumber}
                                  </button>
                                </li>
                              );
                            } else if (
                              pageNumber === currentPage - 2 ||
                              pageNumber === currentPage + 2
                            ) {
                              return (
                                <li key={pageNumber} className="page-item disabled">
                                  <span className="page-link border-0" style={{ borderRadius: '10px', margin: '0 2px', background: '#f8f9fa', color: '#6c757d' }}>
                                    ...
                                  </span>
                                </li>
                              );
                            }
                            return null;
                          })}
                          
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link border-0" 
                              onClick={handleNextPage}
                              disabled={currentPage === totalPages}
                              style={{ borderRadius: '15px', margin: '0 2px', background: currentPage === totalPages ? '#fbfbfb' : 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)', color: currentPage === totalPages ? '#2a2a2a' : 'white', border: '1px solid rgba(225, 42, 47, 0.2)' }}
                            >
                              Suivant
                              <i className="fas fa-chevron-right ms-1"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="col-12 text-center py-5">
                <div className="card border-0 shadow-lg" style={{ borderRadius: '20px', background: 'linear-gradient(145deg, #fbfbfb 0%, #ffffff 100%)', border: '1px solid rgba(225, 42, 47, 0.1)' }}>
                  <div className="card-body p-5">
                    <div className="mb-4">
                      <i className="fas fa-search fa-4x mb-3" style={{ color: '#e12a2f', opacity: '0.6' }}></i>
                    </div>
                    <h3 className="mb-3" style={{ color: '#2a2a2a' }}>{t('patents.noPatentsFound')}</h3>
                    <p className="mb-4" style={{ color: '#2a2a2a' }}>{t('patents.tryDifferentCriteria')}</p>
                    <div className="row justify-content-center">
                      <div className="col-md-8">
                        <div className="alert border-0" style={{ borderRadius: '15px', background: 'rgba(225, 42, 47, 0.1)', border: '1px solid rgba(225, 42, 47, 0.2)' }}>
                          <h6 className="alert-heading" style={{ color: '#e12a2f' }}>
                            <i className="fas fa-lightbulb me-2"></i>
                            {t('patents.searchTips')}
                          </h6>
                          <ul className="mb-0 text-start">
                            <li>{t('patents.expandYearRange')}</li>
                            <li>{t('patents.tryDifferentKeywords')}</li>
                            <li>{t('patents.changeOwnerType')}</li>
                            <li>{t('patents.modifyPctFilter')}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ConferenceProceedings;